import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useGlass } from 'styles/glass';

/**
 * Liquid-glass tab bar: a glass trough with a brand-filled capsule that
 * slides to the selected tab.
 *
 * Why the capsule is filled with the brand colour rather than frosted: on a
 * phone, a colourless frosted pill and an unselected tab are nearly the same
 * value, and "which tab am I on" becomes a guess. The fill answers it from
 * across the room; the rim and the bloom are what keep it reading as a lit
 * pane of glass instead of a flat chip.
 *
 * Drop-in for MUI <Tabs>: `onChange` is called `(event, value)`, so an
 * existing `onChange={(_, v) => setTab(v)}` call site moves over unchanged.
 *
 *   <GlassTabs value={tab} onChange={(_, v) => setTab(v)}
 *              tabs={['Information', 'Logo', 'Bank Info']} />
 *
 * Tabs may be strings, or objects for richer entries:
 *
 *   tabs={[{ label: 'Library', icon: <PhotoIcon /> }, …]}
 */
export default function GlassTabs({ value = 0, onChange, tabs = [], sx }) {
  const GLASS = useGlass();
  const items = tabs.map((t) => (typeof t === 'string' ? { label: t } : t));

  const troughRef = useRef(null);
  const tabRefs = useRef([]);
  const [pill, setPill] = useState({ left: 0, width: 0, ready: false });

  // Measure the active tab and park the capsule on it. Runs in a layout
  // effect so the pill is never painted at the wrong place first.
  const measure = useCallback(() => {
    const el = tabRefs.current[value];
    const trough = troughRef.current;
    if (!el || !trough) return;
    setPill({
      // offsetLeft is relative to the trough (it is the offset parent), so
      // this stays correct while the strip is scrolled horizontally.
      left: el.offsetLeft,
      width: el.offsetWidth,
      ready: true,
    });
  }, [value]);

  useLayoutEffect(() => {
    measure();
  }, [measure, items.length]);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    // Catches the container changing width without the window doing so —
    // a drawer opening, a dialog resizing, the sidebar collapsing.
    const ro = new ResizeObserver(measure);
    if (troughRef.current) ro.observe(troughRef.current);
    return () => ro.disconnect();
  }, [measure]);

  // Web fonts land after first paint and change every label's width, which
  // would leave the capsule sitting slightly off its tab until the next
  // interaction.
  useEffect(() => {
    if (!document.fonts?.ready) return undefined;
    let cancelled = false;
    document.fonts.ready.then(() => { if (!cancelled) measure(); });
    return () => { cancelled = true; };
  }, [measure]);

  // On a narrow screen the strip scrolls; keep the selected tab on screen.
  useEffect(() => {
    const el = tabRefs.current[value];
    if (el?.scrollIntoView) {
      el.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    }
  }, [value]);

  const select = (event, index) => {
    if (index === value) return;
    if (onChange) onChange(event, index);
  };

  const handleKeyDown = (event) => {
    const last = items.length - 1;
    let next = null;
    if (event.key === 'ArrowRight') next = value >= last ? 0 : value + 1;
    if (event.key === 'ArrowLeft') next = value <= 0 ? last : value - 1;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = last;
    if (next === null) return;
    event.preventDefault();
    select(event, next);
    tabRefs.current[next]?.focus();
  };

  return (
    <Box
      ref={troughRef}
      role='tablist'
      onKeyDown={handleKeyDown}
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        p: '5px',
        borderRadius: '999px',
        backgroundColor: GLASS.tabTrough,
        backdropFilter: GLASS.buttonBlur,
        WebkitBackdropFilter: GLASS.buttonBlur,
        border: GLASS.tabTroughBorder,
        boxShadow: GLASS.rimSmall,
        // Narrow screens: let the strip scroll rather than squeezing labels
        // into illegibility. The scrollbar itself is noise on a control this
        // small, so it is hidden — the sliding capsule is the affordance.
        maxWidth: '100%',
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        ...sx,
      }}
    >
      {/* The capsule. One absolutely-positioned element that slides, rather
          than a background on each tab — that is what makes the selection
          feel like a single object moving instead of two lights blinking. */}
      <Box
        aria-hidden
        sx={(theme) => ({
          position: 'absolute',
          top: '5px',
          bottom: '5px',
          left: 0,
          width: pill.width,
          transform: `translateX(${pill.left}px)`,
          borderRadius: '999px',
          backgroundColor: alpha(theme.palette.primary.main, GLASS.tabPillAlpha),
          boxShadow: `${GLASS.tabPillGlow}, ${GLASS.tabPillRim}`,
          // Nothing to animate on the very first paint — without this the
          // capsule slides in from the left edge on mount.
          transition: pill.ready
            ? 'transform 320ms cubic-bezier(0.22, 1, 0.36, 1), width 320ms cubic-bezier(0.22, 1, 0.36, 1)'
            : 'none',
          opacity: pill.ready ? 1 : 0,
          pointerEvents: 'none',
        })}
      />

      {items.map((item, index) => {
        const selected = index === value;
        return (
          <Box
            key={item.key ?? item.label ?? index}
            ref={(el) => { tabRefs.current[index] = el; }}
            role='tab'
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            onClick={(event) => select(event, index)}
            sx={{
              position: 'relative', // above the capsule
              zIndex: 1,
              display: 'flex',
              flexDirection: item.icon ? 'column' : 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: item.icon ? '2px' : 0,
              flexShrink: 0,
              px: { xs: '16px', sm: '22px' },
              py: item.icon ? '8px' : '10px',
              borderRadius: '999px',
              cursor: 'pointer',
              userSelect: 'none',
              // Colour is the only thing that transitions here — the capsule
              // does the moving.
              transition: 'color 220ms ease',
              // Selected keeps white: it sits on the brand-filled capsule,
              // which is the same surface the sidebar's active item uses, and
              // the two should not disagree about their ink. Unselected sits
              // on the trough instead, so it follows the mode — hardcoding
              // near-black there put dark text on the dark trough and the
              // labels disappeared.
              color: selected ? '#fff' : GLASS.tabInk,
              '&:hover': selected ? {} : { color: GLASS.tabInkHover },
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: selected ? 'rgba(255,255,255,0.9)' : 'primary.main',
                outlineOffset: '2px',
              },
              '& svg': { fontSize: '20px' },
            }}
          >
            {item.icon}
            <Typography
              component='span'
              sx={{
                fontSize: item.icon ? '12px' : '14px',
                // The weight shift is small on purpose: enough to reinforce
                // the selection, not enough to reflow the strip as the
                // capsule moves.
                fontWeight: selected ? 700 : 500,
                whiteSpace: 'nowrap',
                lineHeight: 1.4,
              }}
            >
              {item.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
